import { Link } from "react-router-dom"

import { LinkInBlankWindow } from "@/components/link-in-blank-window"
import { TronGridBackgroundView } from "@/domains/landing-page/components/tron-grid-background-view"
import { cn } from "@/domains/library"

export const TWITTER_LINK = "https://twitter.com/BuildersAtScale"
export const DISCORD_INVITE_LINK = "https://discord.gg/PqggXcYtYS"
export const DOCS_LINK = "/docs"
export const FARCASTER_LINK = "https://farcaster.xyz/scale-farm"

const FOOTER_TAGLINE = "The MetaIndex for deep liquidity and real yield on Base."

function FooterLink({ to, href, children }: { to?: string; href?: string; children: React.ReactNode }) {
  const className = "text-t200 hover:text-primary-300 transition-colors"
  if (to != null) {
    return (
      <Link to={to} className={className}>
        {children}
      </Link>
    )
  }
  if (href != null) {
    return (
      <a href={href} target="_blank" rel="noreferrer nofollow" className={className}>
        {children}
      </a>
    )
  }
  return null
}
const LANDING_PAGE_GRADIENT_END = "hsl(var(--p500))"
/** When true, gradient matches blog page background (#050505) for a seamless fade on /blog and /blog/*. */
const BLOG_PAGE_GRADIENT_END = "#050505"
/** Uses design token so footer gradient stays in sync with page background (e.g. globals.css --p500: 0 0% 7.84%). */
const P500_GRADIENT_END = "hsl(var(--p500))"

export interface FooterProps {
  /** When true, gradient outer color matches landing page background (#020205) for a seamless fade. */
  isLandingPage?: boolean
  /** When true, gradient matches blog page background (#050505) for a seamless fade. */
  isBlogPage?: boolean
}

const NewFooter = ({ isLandingPage = false, isBlogPage = false }: FooterProps) => {
  const gradientEndColor = isLandingPage
    ? LANDING_PAGE_GRADIENT_END
    : isBlogPage
      ? BLOG_PAGE_GRADIENT_END
      : P500_GRADIENT_END

  const contentMaxWidth = "max-w-[calc(2200px-128px)]"

  return (
    <div className="hey relative z-10 w-full min-w-[320px] text-t100">
      {/* Full-width block: gradient edge-to-edge; content constrained inside */}
      <div className="relative isolate min-h-[360px] w-full overflow-hidden md:aspect-[1920/720] md:max-h-[min(440px,45vh)] md:min-h-[clamp(260px,34vh,360px)] lg:max-h-[min(500px,46vh)] lg:min-h-[clamp(300px,36vh,420px)]">
        {/* Layer 1: Gradient — full viewport width, no max-width */}
        <div className="absolute inset-0 z-0">
          <svg
            className="h-full w-full"
            viewBox="0 0 1920 896"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            preserveAspectRatio="none"
          >
            <g className="footer-gradient-flare" style={{ transformOrigin: "960px 896px" }}>
              <rect width="1920" height="896" fill="url(#footer-grad)" />
            </g>
            <defs>
              <radialGradient
                id="footer-grad"
                cx="0"
                cy="0"
                r="1"
                gradientTransform="matrix(-1528 -30.4959 5.90205 -623.634 960 1102.94)"
                gradientUnits="userSpaceOnUse"
              >
                <stop stopColor="#0000FF" />
                <stop offset="1" stopColor={gradientEndColor} />
              </radialGradient>
            </defs>
          </svg>
        </div>

        <TronGridBackgroundView variant="footer" skipFlatGrid={isLandingPage} />

        {/* Layer 3: Footer content — sticky to top, contained to same max-width */}
        <div className="absolute left-0 right-0 top-0 z-10">
          <div className={cn("mx-auto w-full min-w-[320px] p-6 md:p-8 lg:px-16", contentMaxWidth)}>
            <div className="flex flex-col gap-10 md:flex-row md:justify-between md:gap-12">
              <div className="md:max-w-[320px]">
                <Link to="/" className="inline-block">
                  <img src="/scale/white_full_horizontal.svg" alt="Scale.Farm" className="h-8 w-auto md:h-10" />
                </Link>
                <p className="mt-4 max-w-xs text-sm leading-relaxed text-t200">{FOOTER_TAGLINE}</p>
                <div className="mt-6 flex gap-4">
                  <LinkInBlankWindow
                    href={TWITTER_LINK}
                    className={cn(
                      "flex h-10 w-10 shrink-0 items-center justify-center rounded-full",
                      "bg-p400 text-t200 transition-colors hover:bg-p400-light hover:text-t100",
                    )}
                    aria-label="X (Twitter)"
                  >
                    <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden>
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                    </svg>
                  </LinkInBlankWindow>
                  <LinkInBlankWindow
                    href={DISCORD_INVITE_LINK}
                    className={cn(
                      "flex h-10 w-10 shrink-0 items-center justify-center rounded-full",
                      "bg-p400 text-t200 transition-colors hover:bg-p400-light hover:text-t100",
                    )}
                    aria-label="Discord"
                  >
                    <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden>
                      <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.086 2.176 2.419 0 1.334-.966 2.419-2.176 2.419zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.086 2.176 2.419 0 1.334-.966 2.419-2.176 2.419z" />
                    </svg>
                  </LinkInBlankWindow>
                </div>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-8 sm:grid-cols-3 sm:gap-10 md:mt-0 md:gap-24">
                <div>
                  <h4 className="mb-4 font-bold text-t100">App</h4>
                  <ul className="space-y-4 text-sm">
                    <li>
                      <FooterLink to="/dashboard">Dashboard</FooterLink>
                    </li>
                    <li>
                      <FooterLink to="/swap">Swap</FooterLink>
                    </li>
                    <li>
                      <FooterLink to="/liquidity">Liquidity</FooterLink>
                    </li>
                    <li>
                      <FooterLink to="/lock">veSCALE</FooterLink>
                    </li>
                    <li>
                      <FooterLink to="/vote">Vote</FooterLink>
                    </li>
                  </ul>
                </div>
                <div>
                  <h4 className="mb-4 font-bold text-t100">Resources</h4>
                  <ul className="space-y-4 text-sm">
                    <li>
                      <FooterLink to={DOCS_LINK}>Docs</FooterLink>
                    </li>
                    <li>
                      <FooterLink to="/analytics">Analytics</FooterLink>
                    </li>
                    <li>
                      <FooterLink to="/treasury">Treasury</FooterLink>
                    </li>
                    <li>
                      <FooterLink to={DOCS_LINK}>Brand Assets</FooterLink>
                    </li>
                  </ul>
                </div>
                <div>
                  <h4 className="mb-4 font-bold text-t100">Community</h4>
                  <ul className="space-y-4 text-sm">
                    <li>
                      <FooterLink href={DISCORD_INVITE_LINK}>Discord</FooterLink>
                    </li>
                    <li>
                      <FooterLink href={TWITTER_LINK}>Twitter</FooterLink>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

const Footer = NewFooter

export default Footer
